import { AnimatePresence, motion } from "framer-motion";
import {
  ReactNode,
  useRef,
  useState,
  useLayoutEffect,
  useEffect,
  useTransition,
} from "react";

interface ISlideDownUpAnimation {
  children: ReactNode;
  show?: boolean;
  keepAlive?: boolean;
}

const SlideDownUpAnimation: React.FC<ISlideDownUpAnimation> = ({
  children,
  show = true,
  keepAlive = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  const [isPending, startTransition] = useTransition();
  const [height, setHeight] = useState<number | null>(null);
  const hasMounted = useRef(false);
  const prevShow = useRef(show);
  const [internalShow, setInternalShow] = useState(show);

  const updateHeight = () => {
    if (ref.current) {
      const scrollHeight = ref.current.scrollHeight;
      if (scrollHeight !== height) {
        setHeight(scrollHeight);
      }
    }
  };

  useLayoutEffect(() => {
    if (show) {
      startTransition(() => updateHeight());
    }
  }, [children, show]);

  useEffect(() => {
    if (!ref.current || !show) return;

    const observer = new ResizeObserver(() =>
      startTransition(() => updateHeight())
    );
    observer.observe(ref.current);

    return () => observer.disconnect();
  }, [show]);

  const shouldAnimate = hasMounted.current && prevShow.current !== show;

  useEffect(() => {
    hasMounted.current = true;
    prevShow.current = show;
    if (keepAlive && show) {
      setInternalShow(true);
    }
  }, [show, keepAlive]);

  const handleAnimationComplete = () => {
    if (keepAlive && !show) {
      setInternalShow(false); // oculta visualmente luego de la animación
    }
  };

  const content = (
    <motion.div
      key="slide"
      initial={shouldAnimate && show ? { opacity: 0, height: 0 } : false}
      animate={
        show
          ? { opacity: 1, height: height ?? "auto" }
          : { opacity: 0, height: 0 }
      }
      exit={{ opacity: 0, height: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
      style={{ overflow: "hidden" }}
      onAnimationComplete={handleAnimationComplete}
    >
      <div
        ref={ref}
        style={{
          visibility:
            !show && keepAlive && !internalShow ? "hidden" : "visible",
        }}
      >
        {children}
      </div>
    </motion.div>
  );

  return keepAlive ? (
    content
  ) : (
    <AnimatePresence mode="wait">
      {(show || shouldAnimate) && content}
    </AnimatePresence>
  );
};

export default SlideDownUpAnimation;
